import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { User, Phone, Building2, Home, Search, Handshake, X } from "lucide-react";

const ROLES = [
  {
    value: "particulier",
    label: "Bailleur / Propriétaire",
    desc: "Je veux vendre ou louer un bien immobilier",
    icon: Home,
    color: "#FF6B1A",
  },
  {
    value: "demarcheur",
    label: "Locataire / Acheteur",
    desc: "Je cherche un bien à louer ou à acheter",
    icon: Search,
    color: "#00B4FF",
  },
  {
    value: "agence",
    label: "Agence immobilière",
    desc: "Je représente une agence immobilière",
    icon: Building2,
    color: "#8B5CF6",
  },
  {
    value: "promoteur",
    label: "Promoteur immobilier",
    desc: "Je développe des projets immobiliers",
    icon: Handshake,
    color: "#10B981",
  },
];

const OnboardingModal = ({ user, onComplete }) => {
  const [step, setStep] = useState(1);
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("");
  const [agencyName, setAgencyName] = useState("");
  const [loading, setLoading] = useState(false);

  const canNext = step === 1 ? name.trim().length >= 2 : role !== "";
  const showAgency = role === "agence" || role === "promoteur";

  const submit = async () => {
    if (!role) { toast.error("Veuillez choisir votre rôle"); return; }
    setLoading(true);
    try {
      const payload = { role, name: name.trim() };
      if (phone.trim()) payload.phone = phone.trim();
      if (agencyName.trim()) payload.agency_name = agencyName.trim();
      await api.put("/auth/profile", payload);
      toast.success("Bienvenue sur IMORA Tchad !");
      onComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-[#0A0A0A] text-white p-6">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <User className="h-5 w-5 text-[#FF6B1A]" />
              <span className="font-heading font-black text-lg">Complétez votre profil</span>
            </div>
            <span className="text-xs text-white/50">{step}/2</span>
          </div>
          <p className="text-sm text-white/60">Pour accéder à toutes les fonctionnalités d'IMORA Tchad</p>
          {/* Progress */}
          <div className="mt-3 h-1 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#FF6B1A] rounded-full transition-all duration-500"
              style={{ width: step === 1 ? "50%" : "100%" }}
            />
          </div>
        </div>

        <div className="p-6">
          {step === 1 ? (
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-neutral-600 uppercase tracking-wider">Nom complet</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="onboarding-name"
                  placeholder="Votre nom complet"
                  className="mt-1 w-full h-12 px-4 border-2 border-neutral-200 rounded-lg focus:border-[#FF6B1A] focus:outline-none text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-neutral-600 uppercase tracking-wider">
                  Téléphone Tchad (+235)
                </label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-neutral-500 font-semibold">+235</span>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    data-testid="onboarding-phone"
                    placeholder="64 00 00 00"
                    className="w-full h-12 pl-14 pr-4 border-2 border-neutral-200 rounded-lg focus:border-[#FF6B1A] focus:outline-none text-sm"
                  />
                </div>
              </div>
              <button
                onClick={() => setStep(2)}
                disabled={!canNext}
                data-testid="onboarding-next-btn"
                className="w-full h-12 bg-[#FF6B1A] hover:bg-[#E65A10] text-white font-bold rounded-lg transition disabled:opacity-50"
              >
                Suivant →
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm font-semibold text-neutral-700">Quel est votre profil ?</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {ROLES.map((r) => {
                  const Icon = r.icon;
                  return (
                    <button
                      key={r.value}
                      onClick={() => setRole(r.value)}
                      data-testid={`role-${r.value}`}
                      className={`text-left p-4 rounded-xl border-2 transition ${
                        role === r.value
                          ? "border-[#FF6B1A] bg-[#FF6B1A]/5"
                          : "border-neutral-200 hover:border-neutral-400"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className="h-4 w-4" style={{ color: r.color }} />
                        <span className="font-heading font-bold text-sm">{r.label}</span>
                      </div>
                      <p className="text-xs text-neutral-500">{r.desc}</p>
                    </button>
                  );
                })}
              </div>

              {showAgency && (
                <div>
                  <label className="text-xs font-bold text-neutral-600 uppercase">Nom de l'agence / entreprise</label>
                  <input
                    type="text"
                    value={agencyName}
                    onChange={(e) => setAgencyName(e.target.value)}
                    data-testid="onboarding-agency"
                    placeholder="Ex : Immo Tchad SARL"
                    className="mt-1 w-full h-12 px-4 border-2 border-neutral-200 rounded-lg focus:border-[#FF6B1A] focus:outline-none text-sm"
                  />
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => setStep(1)}
                  className="h-12 px-6 border-2 border-neutral-200 rounded-lg font-bold text-sm hover:bg-neutral-50"
                >
                  ← Retour
                </button>
                <button
                  onClick={submit}
                  disabled={loading || !role}
                  data-testid="onboarding-submit-btn"
                  className="flex-1 h-12 bg-[#0A0A0A] hover:bg-black text-white font-bold rounded-lg transition disabled:opacity-50"
                >
                  {loading ? "Enregistrement…" : "Accéder à IMORA Tchad"}
                </button>
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-neutral-400 pb-4">
          Vous pouvez modifier ces informations à tout moment dans votre profil
        </p>
      </div>
    </div>
  );
};

export default OnboardingModal;
